import React, {useState, useEffect, useRef} from "react";
import {GridContainer, gridConstants, GridCell} from "../../../Components/Grid";
import {
    Button, CheckBoxField,
    clearInputFormErrorFields,
    convertInputFormToObject,
    EmailField,
    InputForm,
    RadioList,
    SelectField,
    SwitchFieldset,
    TextField,
    useInputForm,
} from "../../../Components/Inputs";
import {reinitializeSpecificInputFormFields} from "../../../Components/Inputs/inputUtility";
import {userDetailsModel} from "./userDetailsFactory";
import {createListFromArrayOfObjects} from "../../../Utilities/Types/listUtilities";
import {UserDetailsPasswordFields} from "./UserDetailsPasswordFields";
import {
    canUserUpdateApprovalStatus,
    createPatronRoleId,
    displayDelegateCheckbox,
    getWarningMessageForUserDetailsForm,
    isUserDetailsFormValid,
    isLoggedInUserSameAsPatron,
    canParentTypeHaveAnOrganization
} from "./userDetailsUtilities";
import {isNullOrUndefined} from "../../../Utilities/commonUtilities";
import {
    AGENCY_STAFF_MEMBER_TYPES_LIST,
    CHARTER_SCHOOL_EMPLOYEE_TYPES_LIST,
    CHARTER_TYPE,
    CHARTER_TYPE_LIST,
    DISTRICT_EMPLOYEE_TYPES_LIST,
    FIMC_SUB_TYPES_LIST,
    JOB_ROLES,
    OTHER_TYPES_LIST,
    PARENT_TYPES,
    PARENT_TYPES_LIST,
    PRIVATE_SCHOOL_EMPLOYEE_TYPES_LIST
} from "../../../SiteConfig/jobTitles";
import {Allow, policyEvents} from "../../../Components/Authorize";
import {APPROVAL_STATUS, APPROVAL_STATUS_LIST} from "../approvalConstants";
import {ACTIVE_STATUS_LIST} from "../activeConstants";
import { isAdmin } from "../../../Utilities/LocalStorage/storageUtilities";

import type {User} from "../../../types";
import type {ListItem} from "../../../Utilities/Types/listUtilities";

interface UserDetailsProps {
    askToUpdatePassword?: boolean;
    districts: { id: string; districtName: string }[];
    handleCancel?: () => void;
    handleManageUser: (user: Record<string, unknown>) => void;
    isEditing?: boolean;
    isLoading?: boolean;
    jobTitles: { jobID: number; jobTitle: string }[];
    patron: User;
    setWarnings: (warnings: string[]) => void;
}

export const UserDetails: React.FC<UserDetailsProps> = ({
                         askToUpdatePassword,
                         districts,
                         handleCancel,
                         handleManageUser,
                         isEditing = false,
                         jobTitles,
                         patron,
                         setWarnings,
                     }) => {

    const prevJobTitle = useRef(String(patron.jobTitle ?? ''));
    const prevPatronSubType = useRef(String(patron.patronSubType ?? ''));

    const userDetailsForm = useInputForm(userDetailsModel({...patron, ...{askToUpdatePassword}}));

    const [enablePasswordFields, setEnablePasswordFields] = useState(
        !userDetailsForm.askToUpdatePassword.value
    );

    const isFormValidAndCheckForWarning = () => {
        const isFormValid = isUserDetailsFormValid(userDetailsForm, setWarnings, enablePasswordFields, isEditing, patron);

        if (isFormValid && isEditing && !isAdmin()) {
            let warningMessages = getWarningMessageForUserDetailsForm(userDetailsForm, patron);

            if (warningMessages.length > 0) {
                const message = `Please note:\n\n${warningMessages.join('\n\n')} \n\nAre you sure you want to continue?`;
                return confirm(message);
            }
        }

        return isFormValid;
    };

    const handleClickSubmit = () => {
        if (isFormValidAndCheckForWarning()) {

            if (!isEditing)
                createPatronRoleId(userDetailsForm);
            else if (!displayDelegateCheckbox(userDetailsForm, patron))
                resetFormFields(["isDelegate"], "");

            const user = convertInputFormToObject(userDetailsForm);

            handleManageUser(user);
        }
    };

    const handleClickCancel = () => {
        handleCancel?.();
    }

    const handleUpdatePasswordCheck = () => {
        setEnablePasswordFields(!enablePasswordFields);
        userDetailsForm.updatePassword.setValue(
            userDetailsForm.updatePassword.value === 'true' ? 'false' : 'true'
        );
    };

    const resetFormFields = (clearValuesForFields: string[], newValue = "") => {
        reinitializeSpecificInputFormFields(userDetailsForm, clearValuesForFields, newValue);
        clearInputFormErrorFields(userDetailsForm);
    };

    useEffect(
        () => {

            if (userDetailsForm.approvalStatus.value !== APPROVAL_STATUS.Approved.toString()) {
                resetFormFields(["isDelegate"], "");
            }

            if (userDetailsForm.email.value === patron.email) {
                resetFormFields(["confirmEmail"]);
            }

            //clears the sub-forms when the job title changes
            if (!isNullOrUndefined(prevJobTitle.current) && prevJobTitle.current !== userDetailsForm.jobTitle.value) {

                resetFormFields(["patronSubType", "organization", "districtId"]);
                prevJobTitle.current = userDetailsForm.jobTitle.value;
            }

            //clears the sub-forms when the patronSubType changes
            if (!isNullOrUndefined(prevPatronSubType.current) && prevPatronSubType.current !== userDetailsForm.patronSubType.value) {

                resetFormFields(["organization", "districtId", "charterStatus"]);
                prevPatronSubType.current = userDetailsForm.patronSubType.value;
            }
        },
        [userDetailsForm.jobTitle.value, userDetailsForm.patronSubType.value, userDetailsForm.email.value, userDetailsForm.approvalStatus.value]
    );

    return (
        <InputForm name={"userDetails"}>

            <TextField {...userDetailsForm.firstName} />

            <TextField {...userDetailsForm.lastName} />

            <EmailField {...userDetailsForm.email} helpText={""}/>

            {
                isEditing && userDetailsForm.email.value !== patron.email &&
                <EmailField {...userDetailsForm.confirmEmail}
                            helpText={`<p style="color: red;">Please note: If you change your email address, you won't be able to log in using your old email. Please use your new email address to access your account.</p>`}/>
            }

            <SelectField
                {...userDetailsForm.jobTitle}
                defaultOption={"Select a Job Title"}
                includeDefaultOption={true}
                options={createListFromArrayOfObjects(jobTitles, "jobID", "jobTitle")}
                label={"Job Title"}
            />

            {
                userDetailsForm.jobTitle.value === JOB_ROLES.Parent &&
                <>
                    <SelectField
                        {...userDetailsForm.patronSubType}
                        defaultOption={"Select a Role"}
                        includeDefaultOption={true}
                        options={PARENT_TYPES_LIST}
                        label={"Role"}
                    />
                    {
                        canParentTypeHaveAnOrganization(userDetailsForm)
                            ? <TextField {...userDetailsForm.organization} />
                            : userDetailsForm.patronSubType.value === PARENT_TYPES.Public.id
                                ? <SelectField
                                    {...userDetailsForm.districtId}
                                    defaultOption={"Select a District"}
                                    includeDefaultOption={true}
                                    options={createListFromArrayOfObjects(
                                        districts,
                                        "id",
                                        "districtName"
                                    )}
                                    label={"District"}
                                />
                                : null
                    }
                </>
            }

            {
                userDetailsForm.jobTitle.value === JOB_ROLES.District &&
                <>
                    <SelectField
                        {...userDetailsForm.patronSubType}
                        defaultOption={"Select a Role"}
                        includeDefaultOption={true}
                        options={DISTRICT_EMPLOYEE_TYPES_LIST}
                        label={"Role"}
                    />

                    {
                        userDetailsForm.patronSubType.value
                            ? <SelectField
                                {...userDetailsForm.districtId}
                                defaultOption={"Select a District"}
                                includeDefaultOption={true}
                                options={createListFromArrayOfObjects(
                                    districts,
                                    "id",
                                    "districtName"
                                )}
                                label={"District"}
                            />
                            : null
                    }

                </>
            }

            {
                userDetailsForm.jobTitle.value === JOB_ROLES.Charter &&
                <>
                    <SelectField
                        {...userDetailsForm.patronSubType}
                        defaultOption={"Select a Role"}
                        includeDefaultOption={true}
                        options={CHARTER_SCHOOL_EMPLOYEE_TYPES_LIST}
                        label={"Role"}
                    />

                    {
                        userDetailsForm.patronSubType.value
                            ? <RadioList
                                {...userDetailsForm.charterType}
                                legend={`Please choose if you are an employee for a Charter LEA or Charter School:`}
                                options={CHARTER_TYPE_LIST}
                            />
                            : null
                    }

                    {
                        userDetailsForm.charterType.value === CHARTER_TYPE.CharterSchool.id
                            ? <>
                                <SelectField
                                    {...userDetailsForm.districtId}
                                    defaultOption={"Select a District"}
                                    includeDefaultOption={true}
                                    options={createListFromArrayOfObjects(
                                        districts,
                                        "id",
                                        "districtName"
                                    )}
                                    label={"District"}
                                />

                                <TextField
                                    {...userDetailsForm.organization}
                                />
                            </>
                            : userDetailsForm.charterType.value === CHARTER_TYPE.CharterLEA.id
                                ? <SelectField
                                    {...userDetailsForm.districtId}
                                    defaultOption={"Select a District"}
                                    includeDefaultOption={true}
                                    options={createListFromArrayOfObjects(
                                        districts,
                                        "id",
                                        "districtName"
                                    )}
                                    label={"District"}
                                />
                                : null
                    }
                </>
            }

            {
                userDetailsForm.jobTitle.value === JOB_ROLES.Private &&
                <>
                    <SelectField
                        {...userDetailsForm.patronSubType}
                        defaultOption={"Select a Role"}
                        includeDefaultOption={true}
                        options={PRIVATE_SCHOOL_EMPLOYEE_TYPES_LIST}
                        label={"Role"}
                    />

                    {
                        userDetailsForm.patronSubType.value
                            ? <TextField
                                {...userDetailsForm.organization}
                            />
                            : null
                    }
                </>
            }

            {
                userDetailsForm.jobTitle.value === JOB_ROLES.Agency &&
                <>
                    <SelectField
                        {...userDetailsForm.patronSubType}
                        defaultOption={"Select a Role"}
                        includeDefaultOption={true}
                        options={AGENCY_STAFF_MEMBER_TYPES_LIST}
                        label={"Role"}
                    />

                    {
                        userDetailsForm.patronSubType.value
                            ? <TextField
                                {...userDetailsForm.organization}
                            />
                            : null
                    }
                </>
            }

            {
                userDetailsForm.jobTitle.value === JOB_ROLES.Fimc &&
                <>
                    <SelectField
                        {...userDetailsForm.patronSubType}
                        defaultOption={"Select a Role"}
                        includeDefaultOption={true}
                        options={FIMC_SUB_TYPES_LIST}
                        label={"Role"}
                    />
                </>
            }

            {
                userDetailsForm.jobTitle.value === JOB_ROLES.Other &&
                <>
                    <SelectField
                        {...userDetailsForm.patronSubType}
                        defaultOption={"Select a Role"}
                        includeDefaultOption={true}
                        options={OTHER_TYPES_LIST}
                        label={"Role"}
                    />
                    {
                        userDetailsForm.patronSubType.value
                            ? <TextField
                                {...userDetailsForm.organization}
                            />
                            : null
                    }
                </>
            }

            {
                !isEditing &&
                <>
                    {
                        userDetailsForm.askToUpdatePassword.value &&
                        <SwitchFieldset
                            {...userDetailsForm.updatePassword}
                            onChange={handleUpdatePasswordCheck}
                            label={`enable password update`}
                            legend={`Update Password`}
                        >
                            <UserDetailsPasswordFields
                                enablePasswordFields={enablePasswordFields}
                                userDetailsForm={userDetailsForm}
                            />
                        </SwitchFieldset>
                    }
                    {
                        !userDetailsForm.askToUpdatePassword.value &&
                        <UserDetailsPasswordFields
                            enablePasswordFields={enablePasswordFields}
                            userDetailsForm={userDetailsForm}
                        />
                    }
                </>
            }

            {
                isEditing &&
                <>
                    <SelectField
                        {...userDetailsForm.approvalStatus}
                        includeDefaultOption
                        options={APPROVAL_STATUS_LIST}
                        defaultOptionValue={""}
                        defaultOption={"Select an approval status"}
                        disabled={!canUserUpdateApprovalStatus(patron)}
                    />

                    {
                        isAdmin() &&
                        <SelectField
                            {...userDetailsForm.isActive}
                            includeDefaultOption={false}
                            options={ACTIVE_STATUS_LIST}
                            label={"Active Status"}
                            disabled={isLoggedInUserSameAsPatron(patron)}
                        />
                    }

                    {
                        displayDelegateCheckbox(userDetailsForm, patron) &&
                        <Allow policyEvent={policyEvents.DELEGATE_USERS}>
                            <CheckBoxField
                                {...userDetailsForm.isDelegate}
                                checked={!!userDetailsForm.isDelegate.value}
                                label={"By checking this box, you authorize this user to act as your delegate. They will have the ability to approve new patrons on your behalf."}
                                value={userDetailsForm.isDelegate.value ? "" : "true"}
                            />
                        </Allow>
                    }
                </>
            }

            <GridCell>
                <GridContainer className={`horizontal-center`} hasMarginX hasMarginY isFlush>
                    <>
                        <GridCell small_columns={isEditing ? gridConstants.column.SIX : gridConstants.column.TWELVE}>
                            <Button
                                isPrimary
                                label={`Submit`}
                                name={`btnSubmit`}
                                onClick={handleClickSubmit}
                            />
                        </GridCell>

                        {
                            isEditing &&
                            <GridCell medium_columns={gridConstants.column.SIX}>
                                <Button
                                    label={`Cancel`}
                                    name={`btnCancel`}
                                    onClick={handleClickCancel}
                                />
                            </GridCell>
                        }
                    </>
                </GridContainer>
            </GridCell>
        </InputForm>
    );
};

